if (!customElements.get('testimonials-component')) {
  class Testimonials extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.testimonials__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);
      this.tabletLargeItems = parseInt(this.dataset.tabletLargeItems);

      this.sliderInstance = false;

      if (!this.enableSlider) return;

      if (this.sliderWrapper) {
        this._sliderWrapperDefaultClass = this.sliderWrapper.className;
      }

      this.init();
      document.addEventListener('matchMobile', () => {
        this.init();
      });
      document.addEventListener('unmatchMobile', () => {
        this.init();
      });
    }

    init() {
      const isBm = this.section && this.section.classList.contains('bm-testimonials');
      /*
       * Sleek default destroys Swiper on mobile so cards fall back to the
       * native `f-grid` layout, which on a 320px-wide, 480px-tall BM card
       * produces the overlapping stack the user reported. For BM we always
       * keep Swiper initialised and re-init on breakpoint change so mobile
       * options (centeredSlides + custom pagination) swap in cleanly.
       */
      if (isBm) {
        if (typeof this.sliderInstance === 'object') {
          this.destroySlider();
        }
        this.initSlider();
      } else if (FoxTheme.config.mqlMobile) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);
      const isBm = this.section && this.section.classList.contains('bm-testimonials');

      const sliderOptions = {
        slidesPerView: 2,
        spaceBetween: columnGap.mobile,
        navigation: {
          nextEl: this.section.querySelector(this.selectors.nextEl),
          prevEl: this.section.querySelector(this.selectors.prevEl),
        },
        pagination: this.section.querySelector(this.selectors.pagination)
          ? { el: this.section.querySelector(this.selectors.pagination), clickable: true }
          : false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.tabletItems),
            spaceBetween: columnGap.tablet
          },
          1024: {
            slidesPerView: parseInt(this.tabletLargeItems),
            spaceBetween: columnGap.tabletLarge
          },
          1280: {
            slidesPerView: parseInt(this.items),
            spaceBetween: columnGap.desktop
          },
        },
        loop: true,
        threshold: 2,
      };

      if (isBm) {
        /*
         * ROOT-CAUSE FIX for "overlapping cards on mobile":
         * The section Liquid emits `swipe-mobile swipe-mobile--{N}-cols` on this
         * component and `swipe-mobile__inner` on the wrapper. In theme.css
         * (~line 3420) those classes convert the wrapper into a CSS grid:
         *     grid: auto / auto-flow var(--column-width-mobile);
         * with `--column-width-mobile: 68vw` (1-col default). That grid
         * template sizes every child to 68vw (≈265px at a 390 viewport),
         * which is EXACTLY the slide width we observed in DevTools. Because
         * the width comes from the grid template — not from `width` on the
         * slide itself — every Swiper option (`slidesPerView: 1`) and every
         * CSS `width: 100% !important` rule loses. Strip those classes here
         * for BM sections (mirrors `featured-collection.js`).
         */
        [...this.classList]
          .filter((c) => c === 'swipe-mobile' || c.startsWith('swipe-mobile--'))
          .forEach((c) => this.classList.remove(c));
        if (this.sliderWrapper) {
          this.sliderWrapper.classList.remove('swipe-mobile__inner');
        }

        sliderOptions.slidesPerView = 'auto';
        sliderOptions.spaceBetween = 10;
        Object.keys(sliderOptions.breakpoints).forEach((bp) => {
          sliderOptions.breakpoints[bp].slidesPerView = 'auto';
          sliderOptions.breakpoints[bp].spaceBetween = 10;
        });
        sliderOptions.autoplay = {
          delay: 4000,
          disableOnInteraction: false,
        };

        /*
         * Mobile-only Figma treatment (matchMedia directly — safer than
         * `FoxTheme.config.mqlMobile` which may lag init timing):
         *   - `centeredSlides: true` gives the active 320px card equal peek
         *     on both sides (matches Featured Collection mobile behaviour).
         *   - `pagination.type: 'custom'` + `renderCustom` hard-caps the bar
         *     pagination at 5 segments regardless of slide count. The active
         *     index is mapped proportionally when `total > 5`. Tablet/desktop
         *     breakpoints explicitly opt out via `centeredSlides: false` so
         *     the desktop strip is untouched.
         */
        const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
        if (isMobileViewport) {
          /*
           * Mobile peek (Figma — mirrors bm-featured-collection mobile):
           *   - `slidesPerView: 'auto'` lets each card take its CSS width
           *     (330px, set in section inline style + bm-overrides.css).
           *   - `centeredSlides: true` pins the active 330px card dead-centre
           *     in the 390px-wide viewport, giving ~30px peek on each side of
           *     neighbouring cards — the exact treatment on Featured Collection.
           *   - `spaceBetween: 10` matches the gap used by featured-collection.js.
           *   - `loop: true` is safe here because the swipe-mobile CSS grid is
           *     now stripped (above), so Swiper's own slide math is authoritative.
           * Pagination is still JS-capped at 5 bullets via `type: 'custom'`.
           * Tablet/desktop are unchanged — the breakpoints block below explicitly
           * opts each back out of centeredSlides to preserve the 3-up desktop row.
           */
          sliderOptions.slidesPerView = 'auto';
          sliderOptions.spaceBetween = 10;
          sliderOptions.centeredSlides = true;
          sliderOptions.loop = true;
          sliderOptions.initialSlide = 0;
          if (sliderOptions.pagination) {
            sliderOptions.pagination.type = 'custom';
            sliderOptions.pagination.renderCustom = (_swiper, current, total) => {
              const MAX = 5;
              const count = Math.min(total, MAX);
              let activeIdx;
              if (total <= MAX) {
                activeIdx = current - 1;
              } else {
                activeIdx = Math.min(
                  Math.floor(((current - 1) / total) * MAX),
                  MAX - 1
                );
              }
              let out = '';
              for (let i = 0; i < count; i++) {
                const cls =
                  i === activeIdx
                    ? 'swiper-pagination-bullet swiper-pagination-bullet-active'
                    : 'swiper-pagination-bullet';
                out += `<span class="${cls}"></span>`;
              }
              return out;
            };
          }
        } else {
          sliderOptions.centeredSlides = false;
        }
        Object.keys(sliderOptions.breakpoints).forEach((bp) => {
          sliderOptions.breakpoints[bp].centeredSlides = false;
        });
      }

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add(this.classes.swiper);
        FoxTheme.utils.stripTailwindGridUtilities(this.sliderWrapper);
        if (isBm) {
          /*
           * `f-grid--gap-*` on `<testimonials-component>` only sets gap CSS variables but can
           * interact oddly with Swiper’s flex host; strip it so the carousel root is a plain block.
           */
          [...this.classList].forEach((c) => {
            if (c.startsWith('f-grid--gap-') || c.startsWith('f-grid--row-gap')) {
              this.classList.remove(c);
            }
          });
        }
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        const extraModules = isBm ? [FoxTheme.Swiper.Autoplay] : null;
        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions, extraModules);
        this.sliderInstance.init();

        if (isBm && this.sliderInstance?.slider) {
          /*
           * BM uses `slidesPerView: 'auto'` + fixed slide widths. If Swiper initialises while the
           * host still has `width: 0` (motion / content-visibility / late font paint), it caches one
           * slide width and the track reads as a single card with empty space. `update()` after
           * layout fixes the blank-rail regression after removing the old `calc((100% - 20px) / 3)`
           * override.
           */
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              try {
                this.sliderInstance.slider.update();
              } catch (e) {
                /* ignore */
              }
            });
          });
        }

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });
      }
      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);

          this.sliderInstance.slider.slideToLoop(index);
        });
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      if (typeof this.sliderInstance === 'object') {
        try {
          this.sliderInstance.slider.destroy(true, true);
        } catch (e) {
          try {
            this.sliderInstance.slider.destroy();
          } catch (e2) {
            console.error('[testimonials] Swiper destroy failed', {
              section_id: this.sectionId,
              error: e2,
              first_error: e,
            });
          }
        }
        this.sliderInstance = false;
      }
      if (this._sliderWrapperDefaultClass) {
        this.sliderWrapper.className = this._sliderWrapperDefaultClass;
      } else {
        this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
        this.sliderWrapper.classList.add(this.classes.grid);
      }
    }
  }
  customElements.define('testimonials-component', Testimonials);
}
